/**
 * Historical Transaction Page
 * 
 * Displays payment request history with filtering and pagination.
 */

import * as React from "react";
import { TransactionFilters } from "./components/TransactionFilters";
import { TransactionTable } from "./components/TransactionTable";
import { useTransactionFilters } from "./hooks/useTransactionFilters";
import { useHistoricalTransactions } from "./hooks/useHistoricalTransactions";
import { useAuth } from "@/contexts/AuthContext";
import { getUserLocationScope } from "@/utils/locationScope";
import { getNumericUserStage } from "@/utils/privileges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { paymentService } from "@/services/paymentService";

function toDateInputValue(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function HistoricalTransactionPage() {
  // Get user for admin assignments
  const { user } = useAuth();

  const isCityAdminStage1 = getNumericUserStage(user) === 1;
  const isProvinceAdminStage2 = getNumericUserStage(user) === 2;
  const isAdmin = isCityAdminStage1 || isProvinceAdminStage2 || getNumericUserStage(user) === 3;

  const [refetchLoading, setRefetchLoading] = React.useState(false);
  const [refetchFrom, setRefetchFrom] = React.useState("2025-04-15");
  const [refetchTo, setRefetchTo] = React.useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toDateInputValue(tomorrow);
  });
  const [refetchLimit, setRefetchLimit] = React.useState("6000");

  const handleMidtransRefetch = React.useCallback(async () => {
    if (!refetchFrom || !refetchTo) {
      toast.error("Tanggal dari dan sampai harus diisi");
      return;
    }

    try {
      setRefetchLoading(true);

      const result = await paymentService.triggerMidtransRefetch(
        refetchFrom,
        refetchTo,
        parseInt(refetchLimit, 10) || 6000
      );

      if (result.success) {
        const summary = result.summary;
        toast.success(
          `Sinkronisasi berhasil!\nPending: ${summary.pendingUpdated} updated, ${summary.pendingSkipped} skipped\nSettlement: ${summary.settlementSynced} synced`,
          { duration: 5000 }
        );
      }
    } catch (error) {
      console.error("Midtrans refetch error:", error);
      toast.error(
        error instanceof Error ? error.message : "Gagal melakukan sinkronisasi Midtrans"
      );
    } finally {
      setRefetchLoading(false);
    }
  }, [refetchFrom, refetchTo, refetchLimit]);

  const [locationScope, setLocationScope] = React.useState<{
    provinces: string[];
    cities: string[];
    level: "nasional" | "province" | "city" | null;
  } | null>(null);

  React.useEffect(() => {
    if (user) {
      getUserLocationScope(user).then((scope) => {
        setLocationScope(scope);
      });
    }
  }, [user]);

  const allowedCities = React.useMemo(() => {
    if (!isCityAdminStage1) return undefined;
    const citiesSet = new Set<string>();
    user?.adminAssignments?.forEach((assignment) => {
      if (!assignment.isActive || assignment.level !== "city") return;
      assignment.cities?.forEach((c) => {
        if (c?.trim()) citiesSet.add(c.trim());
      });
    });
    if (citiesSet.size > 0) return Array.from(citiesSet);
    if (locationScope?.cities?.length) {
      return locationScope.cities.map((c) => c.trim()).filter(Boolean);
    }
    const profileKab = user?.kabupaten?.trim();
    if (profileKab) return [profileKab];
    return undefined;
  }, [isCityAdminStage1, user?.adminAssignments, user?.kabupaten, locationScope?.cities]);

  const allowedProvinces = React.useMemo(() => {
    if (isCityAdminStage1) {
      const p = user?.provinsi?.trim();
      return p ? [p] : undefined;
    }
    if (isProvinceAdminStage2) {
      const provincesSet = new Set<string>();
      user?.adminAssignments?.forEach((assignment) => {
        if (!assignment.isActive || assignment.level !== "province") return;
        assignment.provinces?.forEach((p) => {
          if (p?.trim()) provincesSet.add(p.trim());
        });
      });
      if (provincesSet.size > 0) return Array.from(provincesSet);
      if (locationScope?.provinces?.length) {
        return locationScope.provinces.map((p) => p.trim()).filter(Boolean);
      }
      const profileProv = user?.provinsi?.trim();
      if (profileProv) return [profileProv];
      return undefined;
    }

    if (!user?.adminAssignments || user.adminAssignments.length === 0) {
      return undefined;
    }

    const provinceAssignments = user.adminAssignments.filter(
      (assignment) => assignment.isActive && assignment.level === "province"
    );

    if (provinceAssignments.length === 0) {
      return undefined;
    }

    const provincesSet = new Set<string>();
    provinceAssignments.forEach((assignment) => {
      if (assignment.provinces && Array.isArray(assignment.provinces)) {
        assignment.provinces.forEach((p) => {
          if (p && p.trim()) {
            provincesSet.add(p.trim());
          }
        });
      }
    });

    return provincesSet.size > 0 ? Array.from(provincesSet) : undefined;
  }, [user?.adminAssignments, user?.provinsi, isCityAdminStage1, isProvinceAdminStage2, locationScope?.provinces]);

  const provinceFilterLocked = isProvinceAdminStage2 || isCityAdminStage1;
  const cityFilterLocked = isCityAdminStage1;

  const lockedProvince = React.useMemo(() => {
    if (isCityAdminStage1) {
      return user?.provinsi?.trim() || undefined;
    }
    if (isProvinceAdminStage2 && allowedProvinces?.length === 1) {
      return allowedProvinces[0];
    }

    if (!user?.adminAssignments || user.adminAssignments.length === 0) {
      return undefined;
    }

    const provinceAssignment = user.adminAssignments.find(
      (assignment) => assignment.isActive && assignment.level === "province"
    );

    if (
      provinceAssignment &&
      provinceAssignment.provinces &&
      Array.isArray(provinceAssignment.provinces) &&
      provinceAssignment.provinces.length === 1
    ) {
      const firstProvince = provinceAssignment.provinces[0];
      return firstProvince ? firstProvince.trim() : undefined;
    }

    return undefined;
  }, [user?.adminAssignments, isCityAdminStage1, isProvinceAdminStage2, allowedProvinces]);

  const lockedCity = React.useMemo(() => {
    if (!isCityAdminStage1 || !allowedCities?.length) return undefined;
    if (allowedCities.length === 1) return allowedCities[0];
    const profileKab = user?.kabupaten?.trim();
    if (profileKab && allowedCities.includes(profileKab)) return profileKab;
    return allowedCities[0];
  }, [isCityAdminStage1, allowedCities, user?.kabupaten]);

  // Use a ref to store the setCurrentCursor function so we can call it from the filter change callback
  const setCurrentCursorRef = React.useRef<((cursor: string | null) => void) | null>(null);
  
  // Filters management - pass lockedProvince to prevent it from being cleared
  // Pass onFilterChange callback to reset cursor whenever filters change
  const filters = useTransactionFilters(lockedProvince, () => {
    // Reset cursor whenever filters change
    if (setCurrentCursorRef.current) {
      setCurrentCursorRef.current(null);
    }
  }, lockedCity);
  
  // Transactions data management
  // Use selected province, but fallback to lockedProvince if no province selected
  const transactionsData = useHistoricalTransactions({
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    orderIdSearch: filters.orderIdSearch,
    province: filters.province || lockedProvince,
    city: filters.city || lockedCity,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
  });

  // Store setCurrentCursor in ref so it can be accessed from the filter change callback
  React.useEffect(() => {
    setCurrentCursorRef.current = transactionsData.setCurrentCursor;
  }, [transactionsData.setCurrentCursor]);

  // Ensure locked wilayah is reflected in URL and fetches (admin provinsi / kota)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const currentFromDate = urlParams.get("fromDate") ? new Date(urlParams.get("fromDate")!) : undefined;
    const currentToDate = urlParams.get("toDate") ? new Date(urlParams.get("toDate")!) : undefined;
    const currentOrderId = urlParams.get("orderId") || "";

    if (isCityAdminStage1) {
      if (lockedProvince && lockedCity) {
        filters.setProvince(lockedProvince);
        filters.setCity(lockedCity);
        filters.updateFiltersInUrl(
          currentFromDate,
          currentToDate,
          currentOrderId,
          lockedProvince,
          lockedCity
        );
        const provOk = urlParams.get("province") === lockedProvince;
        const cityOk = urlParams.get("city") === lockedCity;
        if (!provOk || !cityOk) {
          transactionsData.setCurrentCursor(null);
          transactionsData.fetchTransactions("next", null, {
            fromDate: currentFromDate,
            toDate: currentToDate,
            orderIdSearch: currentOrderId,
            province: lockedProvince,
            city: lockedCity,
          });
        }
      }
      return;
    }

    if (lockedProvince) {
      filters.setProvince(lockedProvince);
      const currentCity = urlParams.get("city") || undefined;

      filters.updateFiltersInUrl(
        currentFromDate,
        currentToDate,
        currentOrderId,
        lockedProvince,
        currentCity
      );

      if (!urlParams.has("province") || urlParams.get("province") !== lockedProvince) {
        transactionsData.setCurrentCursor(null);
        transactionsData.fetchTransactions("next", null, {
          fromDate: currentFromDate,
          toDate: currentToDate,
          orderIdSearch: currentOrderId,
          province: lockedProvince,
          city: currentCity,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedProvince, lockedCity, isCityAdminStage1]);

  // Apply filters - use refs to get latest values immediately
  const handleApplyFilters = React.useCallback(() => {
    // Get latest values from refs
    // Use selected province, but fallback to lockedProvince if no province selected
    const selectedProvince = filters.provinceRef.current || lockedProvince;
    const selectedCity = filters.cityRef.current || lockedCity;
    const latestFilters = {
      fromDate: filters.fromDateRef.current,
      toDate: filters.toDateRef.current,
      orderIdSearch: filters.orderIdSearchRef.current,
      province: selectedProvince,
      city: selectedCity,
      sortBy: filters.sortByRef.current,
      sortOrder: filters.sortOrderRef.current,
    };
    
    // Update URL with current filter values from refs (latest values)
    filters.updateFiltersInUrl(
      latestFilters.fromDate,
      latestFilters.toDate,
      latestFilters.orderIdSearch,
      latestFilters.province,
      latestFilters.city
    );
    transactionsData.setCurrentCursor(null);
    // Pass filter overrides directly to use latest values
    transactionsData.fetchTransactions("next", null, latestFilters);
  }, [filters, transactionsData, lockedProvince, lockedCity]);

  // Clear filters
  const handleClearFilters = () => {
    filters.clearFilters();
    transactionsData.setCurrentCursor(null);
    // Use lockedProvince if it exists when clearing
    const filtersToUse: {
      province?: string;
      city?: string;
    } = {};
    if (lockedProvince) filtersToUse.province = lockedProvince;
    if (lockedCity) filtersToUse.city = lockedCity;
    transactionsData.fetchTransactions("next", null, filtersToUse);
  };

  // Handle pagination
  // Note: Due to descending sort order, cursors are swapped:
  // - "next" uses previousCursor (to go forward to newer items)
  // - "previous" uses nextCursor (to go backward to older items)
  const handleNextPage = () => {
    transactionsData.fetchTransactions("next", transactionsData.previousCursor);
    // Update URL with cursor while preserving filters
    if (transactionsData.previousCursor) {
      filters.updateUrlWithCursor(transactionsData.previousCursor, "next");
    }
  };

  const handlePreviousPage = () => {
    transactionsData.fetchTransactions("previous", transactionsData.nextCursor);
    // Update URL with cursor while preserving filters
    if (transactionsData.nextCursor) {
      filters.updateUrlWithCursor(transactionsData.nextCursor, "previous");
    }
  };

  return (
    <div className="flex-1 space-y-4 p-6 overflow-x-hidden max-w-[1400px] mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Riwayat Transaksi</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Data transaksi pembayaran anggota</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              type="date"
              value={refetchFrom}
              onChange={(e) => setRefetchFrom(e.target.value)}
              className="w-36 h-9 text-xs"
              title="Dari tanggal"
            />
            <span className="text-xs text-muted-foreground">s/d</span>
            <Input
              type="date"
              value={refetchTo}
              onChange={(e) => setRefetchTo(e.target.value)}
              className="w-36 h-9 text-xs"
              title="Sampai tanggal"
            />
            <Input
              type="number"
              value={refetchLimit}
              onChange={(e) => setRefetchLimit(e.target.value)}
              className="w-20 h-9 text-xs"
              min={1}
              max={100000}
              title="Limit"
              placeholder="Limit"
            />
            <Button
              onClick={handleMidtransRefetch}
              disabled={refetchLoading}
              variant="outline"
              size="sm"
              className="h-9"
            >
              {refetchLoading ? "Menyinkronisasi..." : "Sinkronisasi Midtrans"}
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <TransactionFilters
        filters={filters}
        loading={transactionsData.loading}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        allowedProvinces={allowedProvinces}
        provinceLocked={provinceFilterLocked}
        allowedCities={allowedCities}
        cityLocked={cityFilterLocked}
      />

      {/* Table */}
      <TransactionTable
        transactions={transactionsData.transactions}
        loading={transactionsData.loading}
        error={transactionsData.error}
        hasNext={transactionsData.hasNext}
        hasPrevious={transactionsData.hasPrevious}
        nextCursor={transactionsData.nextCursor}
        previousCursor={transactionsData.previousCursor}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSortChange={(field, order) => {
          filters.updateSort(field, order);
          transactionsData.setCurrentCursor(null);
          transactionsData.fetchTransactions("next", null);
        }}
      />
    </div>
  );
}

